"use client"

import { lazy, Suspense, useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { normalizeOrderStatus } from "@/lib/order-status"

// Import estável via React.lazy — o wrapper do next/dynamic trocava o tipo de
// componente entre renders e provocava o erro #306 nas transições de estado.
const Lottie = lazy(() =>
  import("lottie-react").then((m: any) => ({
    default: m.default as React.ComponentType<{
      animationData: any
      loop?: boolean
      autoplay?: boolean
      className?: string
    }>,
  })),
)

/** Identidade visual + ritmo de animação de cada estado do pedido. */
const TEMA_POR_FASE: Record<
  string,
  {
    halo: string
    dot: string
    pulseDuration: number
    lottie: "/lottie/waiting.json" | "/lottie/success.json"
    escala: number[]
    mensagens: string[]
  }
> = {
  pendente: {
    halo: "radial-gradient(circle, hsl(45 93% 55% / 0.28), transparent 70%)",
    dot: "hsl(45 93% 47%)",
    pulseDuration: 2.4,
    lottie: "/lottie/waiting.json",
    escala: [0.85, 1.15, 0.85],
    mensagens: [
      "Pedido recebido — a cozinha já sabe!",
      "Um instante... estamos a confirmar tudo.",
      "Respira. O teu momento começa já.",
      "Recebemos tudo certinho, obrigado!",
    ],
  },
  preparando: {
    halo: "radial-gradient(circle, hsl(24 94% 55% / 0.3), transparent 70%)",
    dot: "hsl(24 94% 50%)",
    pulseDuration: 1.2,
    lottie: "/lottie/waiting.json",
    escala: [0.8, 1.25, 0.8],
    mensagens: [
      "Na cozinha há fogo e arte — o teu prato nasce agora.",
      "Os chefs estão a dar o seu melhor.",
      "Aromas a caminho da tua mesa...",
      "Quase lá — o sabor não tem pressa.",
    ],
  },
  entregue: {
    halo: "radial-gradient(circle, hsl(152 76% 44% / 0.3), transparent 70%)",
    dot: "hsl(152 76% 40%)",
    pulseDuration: 2,
    lottie: "/lottie/success.json",
    escala: [1, 1.18, 1],
    mensagens: [
      "Bom apetite! Foi feito com carinho.",
      "Servido! Esperamos que adore cada garfada.",
      "Chegou quentinho — desfruta o momento.",
      "Missão cumprida pela nossa cozinha.",
    ],
  },
  cancelado: {
    halo: "radial-gradient(circle, hsl(0 72% 51% / 0.22), transparent 70%)",
    dot: "hsl(0 72% 51%)",
    pulseDuration: 3,
    lottie: "/lottie/waiting.json",
    escala: [0.9, 1, 0.9],
    mensagens: [
      "Este pedido foi cancelado.",
      "Fala connosco se precisares de ajuda.",
    ],
  },
}

const TEMA_PADRAO = TEMA_POR_FASE.pendente

interface OrderWaitingProps {
  status: string | undefined | null
}

/** Experiência de espera elegante: cores e animação mudam com o estado do pedido. */
export default function OrderWaiting({ status }: OrderWaitingProps) {
  const fase = normalizeOrderStatus(status)
  const tema = TEMA_POR_FASE[fase] ?? TEMA_PADRAO
  const caminho = tema.lottie

  const [mensagemIndex, setMensagemIndex] = useState(0)
  const [animData, setAnimData] = useState<any>(null)
  const [mounted, setMounted] = useState(false)

  const mensagens = useMemo(() => tema.mensagens, [tema])

  // lottie-web só corre no cliente
  useEffect(() => {
    setMounted(true)
  }, [])

  // Carrega a animação Lottie do /public em runtime
  useEffect(() => {
    let activo = true
    setAnimData(null)
    fetch(caminho)
      .then((r) => r.json())
      .then((json) => {
        if (activo) setAnimData(json)
      })
      .catch(() => {})
    return () => {
      activo = false
    }
  }, [caminho])

  useEffect(() => {
    setMensagemIndex(0)
    const interval = setInterval(() => {
      setMensagemIndex((prev) => (prev + 1) % mensagens.length)
    }, 4200)
    return () => clearInterval(interval)
  }, [mensagens])

  return (
    <div className="relative flex flex-col items-center px-6 py-8 text-center">
      {/* Halo que muda de cor com o estado */}
      <motion.div
        key={`halo-${fase}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="pointer-events-none absolute -top-6 size-56 rounded-full blur-3xl"
        style={{ background: tema.halo }}
        aria-hidden="true"
      />

      <motion.div
        key={`${caminho}-${fase}`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 mb-2"
      >
        {mounted && animData ? (
          <Suspense
            fallback={
              <div className="size-32 animate-pulse rounded-full bg-primary/10" />
            }
          >
            <Lottie animationData={animData} loop autoplay className="size-32" />
          </Suspense>
        ) : (
          <div className="size-32 animate-pulse rounded-full bg-primary/10" />
        )}
      </motion.div>

      {/* Mensagem rotativa — própria de cada estado */}
      <motion.p
        key={mensagemIndex}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-xs font-serif text-lg font-light leading-relaxed text-foreground/85"
      >
        {mensagens[mensagemIndex % mensagens.length]}
      </motion.p>

      {/* Pontinhos de respiração — ritmo acelera na cozinha, celebra na entrega */}
      <div className="relative z-10 mt-3 flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={`${fase}-${i}`}
            animate={{
              opacity: [0.25, 1, 0.25],
              scale: tema.escala,
            }}
            transition={{ duration: tema.pulseDuration, repeat: Infinity, delay: i * 0.35 }}
            style={{ backgroundColor: tema.dot }}
            className="size-1.5 rounded-full"
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  )
}
